import { ApplicationsList } from './ApplicationsList';
import { useApplicationsList } from './useApplicationsList';
import { useCallback, useState } from 'react';
import {
	ApplicationSummary,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationId } from '../../../hooks/useApplicationId';
import {
	SCREENING_DASHBOARD,
	INTERVIEW_LANDING,
	CENTRAL_TEAM_DASHBOARD,
} from '../../../shared/Routes';
import { Filter } from './Filter';
import { Button, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBatchId } from '../../../hooks/useBatchId';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import { VerifyResultsModal } from './VerifyResultsModal';
import Icon from '../../../components/ui-components/Icon';
import DashboardIcon from '../../../assets/images/dashboard-icon.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ApplicationsListContainer = () => {
	const { applicationsList, onPageChange, filter, onFilterChange, paging } =
		useApplicationsList({});
	const [openResultsModal, setOpenResultsModal] = useState(false);
	const [applicationToVerify, setApplicationToVerify] =
		useState<ApplicationSummary>();
	const { navigateWithApplicationId } = useApplicationId();
	const { navigateWithBatchId } = useBatchId();
	const { t } = useTranslation();
	const { role } = useRedirectToLogin();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const showScreeningDashboardBtn =
		role !== undefined &&
		[
			UserRole.APPROVER,
			UserRole.ADMIN,
			UserRole.ZC,
			UserRole.SCREENING_ADMIN,
		].includes(role);

	const showCentralTeamDashboard =
		role !== undefined && [UserRole.ADMIN, UserRole.BATCH_ADMIN].includes(role);

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			navigateWithApplicationId(INTERVIEW_LANDING, application.applicationId);
		},
		[navigateWithApplicationId]
	);

	const handleVerifyResultsClick = useCallback(
		(application: ApplicationSummary) => {
			setApplicationToVerify(application);
			setOpenResultsModal(true);
		},
		[]
	);

	const handleScreeningDashboardClick = useCallback(() => {
		navigateWithBatchId(SCREENING_DASHBOARD);
	}, [navigateWithBatchId]);

	const handleCentralTeamDashboardClick = useCallback(() => {
		navigateWithBatchId(CENTRAL_TEAM_DASHBOARD);
	}, [navigateWithBatchId]);

	const filterComponent = filter && (
		<div className="mb-[16px]">
			<Filter value={filter} onFilterChange={onFilterChange} />
		</div>
	);

	const dashboardButtons = (
		<div className="flex gap-2">
			{showScreeningDashboardBtn && (
				<Button
					className="flex-1 h-9 text-primary-blue font-sans font-bold"
					onClick={handleScreeningDashboardClick}
					icon={<Icon src={DashboardIcon} />}
					block={isMobile}
				>
					{t('adminDashboard')}
				</Button>
			)}
			{showCentralTeamDashboard && (
				<Button
					className="flex-1 h-9 text-primary-blue font-sans font-bold"
					onClick={handleCentralTeamDashboardClick}
					icon={<Icon src={DashboardIcon} />}
					block={isMobile}
				>
					{t('centralTeamDashboard')}
				</Button>
			)}
		</div>
	);

	return (
		<div className="flex flex-col p-[16px]">
			<div className="flex gap-8">
				<Text className="hidden md:block font-sans font-bold text-xl text-secondary-black mb-4 whitespace-nowrap">
					{t('listOfCandidates')}
				</Text>
				<div className="flex flex-col w-full">
					{showScreeningDashboardBtn && isMobile && (
						<Button
							onClick={handleScreeningDashboardClick}
							className={`text-center ${
								isMobile
									? 'w-full mb-4 text-primary-blue font-bold'
									: 'underline hover:underline-offset-4 text-primary-blue font-bold p-0'
							}`}
							block={isMobile}
						>
							{t('adminDashboard')}
						</Button>
					)}
					{showCentralTeamDashboard && isMobile && (
						<Button
							onClick={handleCentralTeamDashboardClick}
							className={`text-center ${
								isMobile
									? 'w-full mb-4 text-primary-blue font-bold'
									: 'underline hover:underline-offset-4 text-primary-blue font-bold p-0'
							}`}
							block={isMobile}
						>
							{t('centralTeamDashboard')}
						</Button>
					)}
				</div>
			</div>
			{isMobile ? (
				<>{filterComponent}</>
			) : (
				<div className="flex items-center justify-between">
					{filterComponent}
					{dashboardButtons}
				</div>
			)}
			<ApplicationsList
				data={applicationsList}
				onPageChange={onPageChange}
				onEvaluateClick={handleEvaluateClick}
				onVerifyResultsClick={handleVerifyResultsClick}
				pageConfig={paging}
			/>
			<VerifyResultsModal
				openModal={openResultsModal}
				onClose={() => setOpenResultsModal(false)}
				application={applicationToVerify}
			/>
		</div>
	);
};
